/**
 * Content constants for UI components
 *
 * Centralized content constants used across the application.
 */

export const CONTENT_CONSTANTS = {
  gridLayout: {
    /** Number of columns in the content grid */
    columnsCount: 3,

    /**
     * Number of columns in the content grid when the viewport is in landscape
     * orientation. Web-only enhancement (no Flutter parity — the LIVE Flutter
     * app uses a fixed 3-column grid). Portrait/default stays `columnsCount: 3`.
     *
     * Applies to **page** grids only: a grid rendered inside a `SheetService`
     * surface (bottom sheet or dialog) always uses `columnsCount`, because the
     * sheet is width-constrained and 5 columns leave tiles too short for their
     * content. That choice is resolved centrally by `useGridColumnsCount`, so
     * no call site selects between the two values.
     */
    landscapeColumnsCount: 5,

    /** Spacing between grid items on the cross axis in pixels */
    crossAxisSpacing: 2,

    /** Spacing between grid items on the main axis in pixels */
    mainAxisSpacing: 2,

    /** Aspect ratio for grid items (width / height) */
    itemAspectRatio: 1,

    /** Number of buffer rows to render outside the visible area for smoother scrolling */
    bufferRows: 3,
  },

  /**
   * Portrait (width / height) fallback aspect ratio (3/4) the full-page media
   * viewer (`DefaultMediaPage`) uses for its thumbnail box on a mobile portrait
   * viewport, where the media's own (often wider) ratio would leave the box too
   * short. Numeric form so it multiplies cleanly inside `calc()` when deriving a
   * max-width from `defaultMediaMaxHeight` (`width = height * ratio`).
   *
   * The feedback screenshots grid no longer references this — it reuses the
   * shared `gridLayout` tokens; this is solely the media viewer's portrait
   * fallback.
   */
  feedbackAspectRatioValue: 3 / 4,

  /**
   * Aspect ratio (width / height) for a content "gifts received" grid tile —
   * the circular-avatar gifter cell. Mirrors Flutter's
   * `gridThreeColumnsCircleBorderAvatarAspectRatio` used by the gifts grid
   * (content_gifts_bottom_sheet.dart gridView / grid_widget.dart). Numeric form
   * so it feeds the `VirtualList` grid `itemAspectRatio` prop directly.
   */
  giftGiverAspectRatioValue: 3 / 4,

  /**
   * Landscape (width / height) aspect ratio (21:9) the full-page media viewer
   * (`DefaultMediaPage`) caps a landscape thumbnail box at — per
   * `VideoComponent.getVideoWrapperStyle`'s 21:9 ceiling — so a wide screenshot
   * never renders an overly tall box. String form for the CSS `aspectRatio`
   * property.
   */
  feedbackLandscapeAspectRatio: '21 / 9',

  /**
   * Square (width / height) aspect ratio for a media/icon box that must stay
   * square while it is free to shrink — the send-gift tile's gift-icon box
   * (`GiftGridItem`), whose height flexes inside a fixed-height `VirtualList`
   * grid cell while `object-fit: cover` must never centre-crop the (square)
   * gift artwork. String form for the CSS `aspectRatio` property.
   */
  squareAspectRatio: '1 / 1',

  /**
   * Max height the full-page media viewer's (`DefaultMediaPage`) thumbnail box
   * may occupy, expressed as a CSS length. Matches
   * `VideoComponent.getVideoWrapperStyle`'s `85vh` portrait ceiling; the
   * matching max-width is derived from the media's aspect ratio
   * (`calc(85vh * ratio)`) so the box keeps its ratio (no distortion) while
   * never overflowing the viewport.
   */
  defaultMediaMaxHeight: '85vh',

  /**
   * Aspect ratio (height / width) of the 1:1 video-call self-view PiP box —
   * Flutter `height = 4 / 3 * width` (`video_call_screen.dart:962`, remote-view
   * counterpart `:1019`). Numeric form so it multiplies cleanly when deriving
   * the PiP height from its responsive width (`height = width * ratio`).
   */
  videoCallPipAspectRatio: 4 / 3,

  /**
   * How early a server-paginated list starts fetching its next page, as a
   * fraction of the viewport height — fed to `VirtualList`'s
   * `reachedEndThreshold`. Mirrors Flutter's load-more delta
   * `MediaQuery.of(context).size.height * .2`
   * (lib/presentation/bottom_sheet/search_users_bottom_sheet.dart:215-216).
   * Unitless on purpose: the viewport half comes from `useViewportDimensions`.
   */
  loadMoreViewportFraction: 0.2,
} as const;

export type ContentConstants = typeof CONTENT_CONSTANTS;

/**
 * Constant prefix on every media `path` value stored in Firestore
 * (URL-encoded form of `media/`). Trimmed off content URLs and re-prepended
 * in the page so the address bar stays short.
 */
export const MEDIA_PATH_PREFIX_ENCODED = 'media%2F';

/**
 * Constant trailing separator on every media `path` value stored in Firestore
 * (URL-encoded form of the trailing `/`). Trimmed off content URLs and
 * re-appended in the page.
 */
export const MEDIA_PATH_SUFFIX_ENCODED = '%2F';

/**
 * Bounding envelope for the small media thumbnail rendered inside a comment
 * row when the comment is attached to a media (Flutter parity:
 * `commentMediaWidget` in `lib/presentation/custom_widgets/comment_widget.dart`).
 *
 * These are caps, not the rendered size: the box actually drawn is derived from
 * the media's own `aspectRatio` (`getRatioPreservingBoxSize`) and only bounded
 * by these two, so the thumbnail always keeps the media's ratio — matching the
 * profile grid (`ContentGridItem`) — instead of centre-cropping it into a fixed
 * portrait tile.
 */
export const COMMENT_MEDIA_THUMBNAIL_MAX_WIDTH = 90;
export const COMMENT_MEDIA_THUMBNAIL_MAX_HEIGHT = 120;
